/**
 * Year-End Review Firestore Cache Utilities
 *
 * 1회 발행 정책:
 * - isPublished === true 인 캐시만 유효
 * - 100% 완료(Friends 포함) 시에만 발행
 * - 발행 후 재계산 없음
 */

import { dbService } from '../firebaseInstance';
import firebase from 'firebase/compat/app';

/**
 * Firestore에서 발행된 연말정산 데이터 조회
 * @param {string} userId - 사용자 ID
 * @param {number} year - 연도 (예: 2024)
 * @returns {Promise<object|null>} 캐시 데이터 또는 null
 */
export const getYearEndCache = async (userId, year) => {
  if (!userId || !year) {
    console.warn('getYearEndCache: userId or year is missing');
    return null;
  }

  try {
    const docRef = dbService
      .collection('user')
      .doc(String(userId))
      .collection('yearEndSummary')
      .doc(String(year));

    const docSnap = await docRef.get();

    if (!docSnap.exists) {
      return null;
    }

    const data = docSnap.data();

    // isPublished 플래그 확인
    if (!data.isPublished) {
      console.log('Year-end cache exists but not published yet');
      return null;
    }

    return data;
  } catch (error) {
    console.warn('Year-end cache read failed:', error);
    return null;
  }
};

/**
 * 연말정산 리포트 1회 발행 (100% 완료 시에만 호출)
 *
 * @param {string} userId - 사용자 ID
 * @param {number} year - 연도
 * @param {object} data - 발행할 데이터 (isPublished: true 필수)
 * @returns {Promise<boolean>} 발행 성공 여부
 */
export const publishYearEndReport = async (userId, year, data) => {
  if (!userId || !year) {
    console.error('publishYearEndReport: userId or year is missing');
    return false;
  }

  // 발행 플래그 검증
  if (!data.isPublished) {
    console.error('Cannot publish: isPublished flag must be true');
    return false;
  }

  // Friends 데이터 검증 (발행 조건)
  if (!Array.isArray(data.friendsOfTheYear)) {
    console.error('Cannot publish: friendsOfTheYear must be an array');
    return false;
  }

  // 필수 데이터 검증
  if (!data.stats || typeof data.stats.totalThreads !== 'number') {
    console.error('Cannot publish: stats.totalThreads is required');
    return false;
  }

  try {
    const docRef = dbService
      .collection('user')
      .doc(String(userId))
      .collection('yearEndSummary')
      .doc(String(year));

    // merge 없이 전체 덮어쓰기 (1회 발행 정책)
    await docRef.set(data);

    console.log(`Year-end report published for user ${userId}, year ${year}`);
    return true;
  } catch (error) {
    console.error('Year-end report publish failed:', error);
    return false;
  }
};

/**
 * 발행 실패 시 로컬 임시 저장 (복구용)
 *
 * @param {number} year - 연도
 * @param {object} data - 임시 저장할 데이터
 */
export const saveYearEndToLocal = (year, data) => {
  try {
    const key = `yearEnd_${year}_pending`;
    localStorage.setItem(key, JSON.stringify({
      ...data,
      savedAt: new Date().toISOString(),
    }));
    console.log(`Year-end data saved to localStorage: ${key}`);
  } catch (error) {
    console.warn('Failed to save year-end data to localStorage:', error);
  }
};

/**
 * 로컬에 임시 저장된 데이터 조회
 *
 * @param {number} year - 연도
 * @returns {object|null} 임시 저장된 데이터 또는 null
 */
export const getYearEndFromLocal = (year) => {
  try {
    const key = `yearEnd_${year}_pending`;
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.warn('Failed to read year-end data from localStorage:', error);
    return null;
  }
};

/**
 * 로컬 임시 저장 데이터 삭제
 *
 * @param {number} year - 연도
 */
export const clearYearEndFromLocal = (year) => {
  try {
    const key = `yearEnd_${year}_pending`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to clear year-end data from localStorage:', error);
  }
};

/**
 * 재시도 로직 포함 발행
 *
 * @param {string} userId - 사용자 ID
 * @param {number} year - 연도
 * @param {object} data - 발행할 데이터
 * @param {number} maxRetries - 최대 재시도 횟수 (기본값: 3)
 * @returns {Promise<boolean>} 발행 성공 여부
 */
export const publishWithRetry = async (userId, year, data, maxRetries = 3) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const success = await publishYearEndReport(userId, year, data);

    if (success) {
      // 로컬 임시 저장 정리
      clearYearEndFromLocal(year);
      return true;
    }

    if (attempt < maxRetries) {
      // 지수 백오프: 1초, 2초, 4초...
      const delay = Math.pow(2, attempt - 1) * 1000;
      console.log(`Publish attempt ${attempt} failed, retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // 최종 실패 시 로컬 임시 저장
  console.warn('All publish attempts failed, saving to localStorage');
  saveYearEndToLocal(year, data);
  return false;
};

// ============================================
// 신규 구조: yearEnd/{id} 컬렉션 함수들
// ============================================

/**
 * 스레드 객체 경량화 (Firestore 저장용)
 * 필수 필드만 유지, 텍스트 200자 제한
 */
const slimThread = (thread) => {
  if (!thread) return null;
  return {
    id: thread.id,
    text: (thread.text || '').slice(0, 200),
    timestamp: thread.timestamp,
    views: thread.views || 0,
    likes: thread.likes || 0,
    replies: thread.replies || 0,
    reposts: thread.reposts || 0,
    permalink: thread.permalink || '',
    media_url: thread.media_url || '',
  };
};

/**
 * yearEndData 경량화 (Firestore 1MB 제한 대응)
 * - 스레드 텍스트 축약
 * - 불필요한 필드 제거
 * - conversations 최소화
 */
const optimizeForStorage = (data) => {
  if (!data) return data;

  const optimized = { ...data };

  // 1. yearOfTheThreads 경량화
  if (optimized.yearOfTheThreads?.threads) {
    optimized.yearOfTheThreads = {
      ...optimized.yearOfTheThreads,
      threads: optimized.yearOfTheThreads.threads.map(item => ({
        ...item,
        thread: slimThread(item.thread),
      })),
    };
  }

  // 2. notableThreads 경량화
  if (optimized.notableThreads) {
    optimized.notableThreads = {
      byViews: (optimized.notableThreads.byViews || []).map(slimThread),
      byReplies: (optimized.notableThreads.byReplies || []).map(slimThread),
      byReposts: (optimized.notableThreads.byReposts || []).map(slimThread),
    };
  }

  // 3. monthlyReport 경량화 (topThreads 4개 유지 - 프론트엔드 요구사항)
  if (optimized.monthlyReport) {
    const slimMonthlyReport = {};
    Object.entries(optimized.monthlyReport).forEach(([month, monthData]) => {
      slimMonthlyReport[month] = {
        month: monthData.month,
        totalThreads: monthData.totalThreads || 0,
        totalViews: monthData.totalViews || 0,
        totalEngagement: monthData.totalEngagement || 0,
        topThreads: (monthData.topThreads || []).slice(0, 4).map(slimThread),
      };
    });
    optimized.monthlyReport = slimMonthlyReport;
  }

  // 4. friendsOfTheYear 경량화 (Top 10 친구)
  // ⚠️ Firestore 1MB 제한 대응 - conversations는 친구당 최대 30개로 제한
  // ⚠️ ConversationView가 conversation.comment.text 구조를 기대함 - 원본 구조 유지!
  if (optimized.friendsOfTheYear) {
    optimized.friendsOfTheYear = optimized.friendsOfTheYear.slice(0, 10).map(friend => ({
      userId: friend.userId || '',
      username: friend.username || '',
      profileImage: friend.profileImage || '',
      interactionCount: friend.interactionCount || 0,
      myRepliesCount: friend.myRepliesCount || 0,
      theirRepliesCount: friend.theirRepliesCount || 0,
      // conversations: 친구당 최대 30개 (Firestore 1MB 제한), 원본 구조 유지
      conversations: (friend.conversations || []).slice(0, 30),
    }));
  }

  return optimized;
};

/**
 * 새 구조에서 yearEnd 문서 생성 또는 업데이트
 * - 기존 ID가 있으면 업데이트 (FORCE_REFRESH 모드)
 * - 없으면 새로 생성하고 user.yearEndIds에 참조 저장
 *
 * @param {string} userId - 사용자 ID
 * @param {number} year - 연도
 * @param {object} data - 연말정산 데이터
 * @param {object} profile - 사용자 프로필 (username, profileImage 등)
 * @param {boolean} forceRefresh - 강제 갱신 여부
 * @returns {Promise<string|null>} 생성/업데이트된 문서 ID
 */
export const createOrUpdateYearEnd = async (userId, year, data, profile, forceRefresh = false) => {
  if (!userId || !year) {
    console.error('createOrUpdateYearEnd: userId or year is missing');
    return null;
  }

  try {
    const userRef = dbService.collection('user').doc(String(userId));
    const userDoc = await userRef.get();
    const userData = userDoc.exists ? userDoc.data() : {};
    const existingId = userData?.yearEndIds?.[String(year)];

    // 데이터 경량화 (Firestore 1MB 제한 대응)
    const optimizedData = optimizeForStorage(data);

    const yearEndData = {
      ...optimizedData,
      userId: String(userId),
      username: profile?.username || '',
      profileImage: profile?.threads_profile_picture_url || '',
      year: Number(year),
      isPublic: true,  // 항상 공개
      viewCount: optimizedData.viewCount ?? 0,
      updatedAt: new Date().toISOString(),
    };

    // 기존 ID가 있고 FORCE_REFRESH 모드인 경우 업데이트
    if (existingId && forceRefresh) {
      const existingRef = dbService.collection('yearEnd').doc(existingId);
      await existingRef.set(yearEndData, { merge: true });
      console.log(`Year-end updated (FORCE_REFRESH): ${existingId}`);
      return existingId;
    }

    // 기존 ID가 있고 재생성 금지 모드면 기존 ID 반환
    if (existingId) {
      console.log(`Year-end already exists: ${existingId}`);
      return existingId;
    }

    // 새 문서 생성
    const newDocRef = dbService.collection('yearEnd').doc();
    await newDocRef.set({
      ...yearEndData,
      createdAt: new Date().toISOString(),
    });

    // user 문서에 yearEndIds 참조 추가
    await userRef.set({
      yearEndIds: { [String(year)]: newDocRef.id }
    }, { merge: true });

    console.log(`Year-end created: ${newDocRef.id}`);
    return newDocRef.id;
  } catch (error) {
    console.error('createOrUpdateYearEnd failed:', error);
    return null;
  }
};

/**
 * yearEnd 문서 ID로 직접 조회 (공유 페이지용)
 *
 * @param {string} id - yearEnd 문서 ID
 * @returns {Promise<object|null>} yearEnd 데이터 또는 null
 */
export const getYearEndById = async (id) => {
  if (!id) {
    console.warn('getYearEndById: id is missing');
    return null;
  }

  try {
    const docRef = dbService.collection('yearEnd').doc(id);
    const docSnap = await docRef.get();

    if (!docSnap.exists) {
      return null;
    }

    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error('getYearEndById failed:', error);
    return null;
  }
};

/**
 * 현재 사용자의 특정 연도 yearEnd 조회
 *
 * @param {string} userId - 사용자 ID
 * @param {number} year - 연도
 * @returns {Promise<object|null>} yearEnd 데이터 또는 null
 */
export const getMyYearEnd = async (userId, year) => {
  if (!userId || !year) {
    console.warn('getMyYearEnd: userId or year is missing');
    return null;
  }

  try {
    const userRef = dbService.collection('user').doc(String(userId));
    const userDoc = await userRef.get();

    if (!userDoc.exists) {
      return null;
    }

    const yearEndId = userDoc.data()?.yearEndIds?.[String(year)];

    if (!yearEndId) {
      return null;
    }

    return getYearEndById(yearEndId);
  } catch (error) {
    console.error('getMyYearEnd failed:', error);
    return null;
  }
};

/**
 * yearEnd 공개 상태 변경
 *
 * @param {string} id - yearEnd 문서 ID
 * @param {boolean} isPublic - 공개 여부
 * @returns {Promise<boolean>} 성공 여부
 */
export const updateYearEndPublic = async (id, isPublic) => {
  if (!id) {
    console.error('updateYearEndPublic: id is missing');
    return false;
  }

  try {
    const docRef = dbService.collection('yearEnd').doc(id);
    await docRef.update({
      isPublic: Boolean(isPublic),
      updatedAt: new Date().toISOString(),
    });

    console.log(`Year-end visibility updated: ${id} -> ${isPublic ? 'public' : 'private'}`);
    return true;
  } catch (error) {
    console.error('updateYearEndPublic failed:', error);
    return false;
  }
};

/**
 * yearEnd 조회수 증가 (비소유자 방문 시)
 *
 * @param {string} id - yearEnd 문서 ID
 * @returns {Promise<boolean>} 성공 여부
 */
export const incrementYearEndViewCount = async (id) => {
  if (!id) {
    console.error('incrementYearEndViewCount: id is missing');
    return false;
  }

  try {
    const docRef = dbService.collection('yearEnd').doc(id);
    await docRef.update({
      viewCount: firebase.firestore.FieldValue.increment(1),
    });

    return true;
  } catch (error) {
    console.error('incrementYearEndViewCount failed:', error);
    return false;
  }
};
